import { useState, useEffect, useCallback } from "react";
import { api, errMsg, unitWord } from "../lib/api";
import { useAuth } from "../App";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ArrowUpDown, Briefcase } from "lucide-react";

const COLS = [
  ["name", "Property"],
  ["bracket_label", "Bracket"],
  ["capacity", "Capacity"],
  ["raw_task_points", "Task pts"],
  ["task_per_capacity", "Per-capacity"],
  ["bracket_rank", "Rank"],
  ["approved_subs", "Approved"],
  ["pending_subs", "Pending"],
  ["support_visits", "Visits"],
  ["reel_status", "Reel"],
  ["last_activity", "Last activity"],
];

const hasActivity = (r) => r.approved_subs + r.pending_subs > 0;

function ReelBadge({ status }) {
  const cls = status === "judged" ? "bg-green-100 text-green-800" : status === "submitted" ? "bg-blue-100 text-blue-800" : "bg-slate-100 text-slate-500";
  return <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase ${cls}`}>{status}</span>;
}

function SummaryCards({ cards }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6 stagger">
      {cards.map(([label, val, id]) => (
        <div key={id} className="border border-slate-200 rounded-md p-3">
          <div className="font-display font-bold text-xl text-[#1a1f36]" data-testid={id}>{val}</div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
        </div>
      ))}
    </div>
  );
}

function PortfolioTable({ rows, clickSort }) {
  return (
    <div className="overflow-x-auto border border-slate-200 rounded-md">
      <table className="w-full text-sm min-w-[900px]" data-testid="portfolio-table">
        <thead>
          <tr className="bg-[#f8f9fc] text-left text-[11px] uppercase tracking-wide text-slate-500">
            {COLS.map(([key, label]) => (
              <th key={key} className="px-3 py-2.5 font-semibold cursor-pointer hover:text-[#1a1f36] whitespace-nowrap" onClick={() => clickSort(key)} data-testid={`pf-sort-${key}`}>
                {label} <ArrowUpDown className="w-3 h-3 inline -mt-0.5 opacity-40" />
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r) => (
            <tr key={r.property_id} className={!hasActivity(r) ? "bg-slate-50/70" : ""} data-testid={`pf-row-${r.property_id}`}>
              <td className="px-3 py-2.5">
                <div className="font-medium text-[#1a1f36]">{r.name}</div>
                <div className="text-[11px] text-slate-500">{r.division}{r.tier ? ` · ${r.tier}` : ""}{r.status === "Needs capacity" ? " · needs capacity" : ""}
                  {!hasActivity(r) && <span className="ml-1.5 px-1.5 py-0.5 bg-slate-200/70 text-slate-600 rounded-full text-[9px] font-bold uppercase" data-testid={`pf-no-activity-${r.property_id}`}>no activity yet</span>}
                </div>
              </td>
              <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{r.bracket_label || "—"}</td>
              <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{r.capacity ? `${r.capacity} ${r.capacity_type}` : "—"}</td>
              <td className="px-3 py-2.5 font-display font-bold text-[#c8102e]">{r.raw_task_points}</td>
              <td className="px-3 py-2.5">{r.task_per_capacity != null ? r.task_per_capacity.toFixed(3) : "—"}</td>
              <td className="px-3 py-2.5 whitespace-nowrap">{r.bracket_rank ? `${r.bracket_rank} of ${r.bracket_size}` : "—"}</td>
              <td className="px-3 py-2.5">{r.approved_subs}</td>
              <td className="px-3 py-2.5">{r.pending_subs > 0 ? <span className="text-[#c8102e] font-semibold">{r.pending_subs}</span> : 0}</td>
              <td className="px-3 py-2.5 whitespace-nowrap">{r.support_visits} of 3</td>
              <td className="px-3 py-2.5"><ReelBadge status={r.reel_status} /></td>
              <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">{r.last_activity ? new Date(r.last_activity).toLocaleDateString() : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Portfolio() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [regionals, setRegionals] = useState([]);
  const [selected, setSelected] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [sort, setSort] = useState({ key: "default", dir: 1 });

  useEffect(() => {
    if (isAdmin) api.get("/regionals/summary").then(({ data }) => setRegionals(data.filter((r) => r.regional !== "Unassigned")));
  }, [isAdmin]);

  const load = useCallback((regional) => {
    setError(null);
    api.get("/portfolio", { params: regional ? { regional } : {} })
      .then(({ data }) => setData(data))
      .catch((err) => { setData(null); setError(errMsg(err)); });
  }, []);

  useEffect(() => {
    if (!isAdmin) load();
  }, [isAdmin, load]);

  const clickSort = (key) => setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: 1 }));

  const rows = data ? [...data.rows].sort((a, b) => {
    if (sort.key === "default") return hasActivity(a) - hasActivity(b) || a.name.localeCompare(b.name);
    const av = a[sort.key], bv = b[sort.key];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    const cmp = typeof av === "number" ? av - bv : String(av).localeCompare(String(bv));
    return cmp * sort.dir;
  }) : [];

  const s = data?.summary;
  const cards = s ? [
    ["On the board", `${s.on_board} of ${s.enrolled}`, "pf-on-board"],
    ["Approved subs", s.approved_subs, "pf-approved"],
    ["Pending subs", s.pending_subs, "pf-pending"],
    ["Engagement", `${s.engaged_pct}%`, "pf-engagement"],
    ["Best Commander", s.commander_rank ? `${s.commander_rank.rank}${["st", "nd", "rd"][s.commander_rank.rank - 1] || "th"} of ${s.commander_rank.of}` : "—", "pf-commander-rank"],
    ["Most Engaged", s.engaged_rank ? `${s.engaged_rank.rank}${["st", "nd", "rd"][s.engaged_rank.rank - 1] || "th"} of ${s.engaged_rank.of}` : "—", "pf-engaged-rank"],
  ] : [];

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 fade-up" data-testid="portfolio-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#c8102e] flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5" />Commander view</p>
          <h1 className="font-display text-3xl font-bold text-[#1a1f36]">{data ? `${data.regional}'s portfolio` : "My Portfolio"}</h1>
          <p className="text-sm text-slate-500 mt-1">Live standings — updates as tasks are approved. Read-only.</p>
        </div>
        {isAdmin && (
          <div className="w-64">
            <Select value={selected} onValueChange={(v) => { setSelected(v); load(v); }}>
              <SelectTrigger data-testid="portfolio-regional-picker"><SelectValue placeholder="Preview a regional's portfolio…" /></SelectTrigger>
              <SelectContent>
                {regionals.map((r) => <SelectItem key={r.regional} value={r.regional} data-testid={`portfolio-pick-${r.regional}`}>{r.regional} ({r.properties})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {error && <div className="border border-slate-200 bg-[#f8f9fc] rounded-md p-6 text-center text-sm text-slate-600" data-testid="portfolio-error">{error}</div>}
      {!data && !error && <div className="p-12 text-center text-slate-500">{isAdmin ? "Pick a regional above to preview their portfolio." : "Loading your portfolio…"}</div>}

      {data && (
        <>
          <SummaryCards cards={cards} />
          <PortfolioTable rows={rows} clickSort={clickSort} />
          <p className="text-xs text-slate-400 mt-2">Task pts include Commander, visit, and Ironman bonuses. Per-capacity = task pts ÷ {unitWord(rows[0]?.capacity_type)}s. Zero-activity properties sort to the top by default — they need a nudge.</p>
        </>
      )}
    </div>
  );
}
